import React, { ReactElement, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import cn from 'classnames';

import { Tooltip } from '@components/common';
import UserSpinner from '@components/common/UserSpinner';

import { IIconProps } from '@icons/interface';

import { convertBytesToSize } from '@utils/fileSizeFormatter';

import { IErrorsType, TFileTypes } from '@interfaces/import/import';

import { EDropzoneFileErrorCodes } from '@enums/dropzone';

import colors from '@constants/colors';

import Meta from '../Meta';

import styles from './Dropzone.module.scss';

interface IDropzoneProps {
	acceptedFileTypes: TFileTypes;
	chooseFileText?: string;
	createUploadUrl?: ({ files }: { files: Array<File> }) => void;
	errors: IErrorsType;
	fileRejectionErrorHandler?: () => void;
	maxFiles?: number;
	maxSize?: number;
	onDropAcceptedHandler?: () => void;
	setSelectedFiles?: (file: Array<File>) => void;
	supportsText?: string;
	uploadIcon: (props?: IIconProps) => ReactElement;
	uploadedIcon: (props?: IIconProps) => ReactElement;
	uploadedText?: string;
	isMultipleFiles?: boolean;
	filteredFiles?: Array<File>;
	maxSizeInfoText?: string;
	chooseFileDescription?: string;
	errorMessage?: string;
	disabled?: boolean;
	isUploadedFilesInfo?: boolean;
}

const Dropzone = ({
	acceptedFileTypes,
	chooseFileText = 'Choose a file',
	chooseFileDescription = 'or drag it here.',
	createUploadUrl,
	errors,
	fileRejectionErrorHandler,
	maxFiles = 0,
	maxSize = Infinity,
	maxSizeInfoText,
	onDropAcceptedHandler,
	setSelectedFiles,
	supportsText,
	uploadIcon,
	uploadedIcon,
	uploadedText = '',
	isMultipleFiles = true,
	filteredFiles,
	errorMessage,
	disabled = false,
	isUploadedFilesInfo = true,
}: IDropzoneProps) => {
	const onDropAccepted = (acceptedFile: Array<File>) => {
		if (onDropAcceptedHandler) onDropAcceptedHandler();
		if (createUploadUrl) createUploadUrl({ files: acceptedFile });
		if (setSelectedFiles) setSelectedFiles(acceptedFile);
	};

	const onDropRejected = () => {
		if (disabled || !fileRejectionErrorHandler) return;

		fileRejectionErrorHandler();
	};

	const validateFile = (file: File) => {
		const allowedExtensions = Object.values(acceptedFileTypes).flat();
		const fileExtension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();

		if (!allowedExtensions.includes(fileExtension)) {
			return {
				code: EDropzoneFileErrorCodes.fileInvalidType,
				message: `File type ${fileExtension} is not accepted`,
			};
		}

		return null;
	};

	const {
		acceptedFiles: dropzoneFiles,
		fileRejections,
		getInputProps,
		getRootProps,
		isDragActive,
	} = useDropzone({
		accept: acceptedFileTypes,
		validator: validateFile,
		maxFiles,
		maxSize,
		onDropAccepted,
		onDropRejected,
		multiple: isMultipleFiles,
		disabled,
	});

	const acceptedFiles = filteredFiles || dropzoneFiles;

	const fileIcon = acceptedFiles.length
		? uploadedIcon({ color: disabled ? colors.gray40 : colors.indigo50 })
		: uploadIcon({ color: disabled ? colors.gray40 : colors.indigo50 });

	const maxSizeText = maxSize !== Infinity
		? maxSizeInfoText ? maxSizeInfoText : `Maximum size: ${convertBytesToSize(maxSize)}`
		: '';

	const errorCodes = useMemo(() => fileRejections.reduce<Array<string>>((acc, file) => {
			const fileErrorCodes = file.errors.map(({ code }) => code);

			fileErrorCodes.forEach((code) => {
				if (!acc.includes(code)) {
					acc.push(code);
				}
			});

			return acc;
		}, [])
		, [fileRejections]);

	const isInfoText = !isUploadedFilesInfo || (!errorCodes.length && !acceptedFiles.length);

	const fileRejectionError = errorCodes.map((code) => {
		switch (code) {
			case EDropzoneFileErrorCodes.tooManyFiles:
				return <div key={code}>{errors[EDropzoneFileErrorCodes.tooManyFiles]}</div>;
			case EDropzoneFileErrorCodes.fileInvalidType:
				return <div key={code}>{errors[EDropzoneFileErrorCodes.fileInvalidType]}</div>;
			case EDropzoneFileErrorCodes.fileTooLarge:
				return <div key={code}>{errors[EDropzoneFileErrorCodes.fileTooLarge]}</div>;
			default:
				return <div key={code}>{code}</div>;
		}
	});

	const fileNamesText = acceptedFiles.length ? acceptedFiles.map((file) => file.name).join(', ') : '';

	const inputProps = getInputProps();

	return (
		<div className={cn(styles.myDropzoneContainer, { [styles.disabled]: disabled })}>
			<div
				{...getRootProps()}
				className={styles.dragAndDrop}
			>
				<input
					data-testid='dropzone'
					{...inputProps}
					disabled={disabled}
				/>
				<div>
					{isDragActive ? <UserSpinner size={25} /> : fileIcon}
				</div>
				<div className={cn(styles.fileName, {
					[styles.active]: !!acceptedFiles.length && isUploadedFilesInfo,
				})}>
					{fileNamesText && isUploadedFilesInfo
						? <Tooltip
							content={fileNamesText}
							isDisabled={!fileNamesText}
							placement='top'
						>
							<span>{fileNamesText}</span>
						</Tooltip>
						: <>
							<span className={cn({ [styles.disabledChooseFileText]: disabled })}>
								{chooseFileText}
							</span> {chooseFileDescription}
						</>
					}
				</div>
				{!!acceptedFiles.length && isUploadedFilesInfo
					&& <div className={styles.uploaded}>
						Your file{acceptedFiles.length > 1 && 's'} {acceptedFiles.length > 1 ? 'have' : 'has'} been
						uploaded.
					</div>
				}
				<div className={cn(styles.errors, { [styles.active]: !!errorCodes.length })}>
					{isInfoText && !errorCodes.length && (
						<>
							{supportsText}
							<br />
							{maxSizeText}
						</>
					)}
					{errorCodes.length
						? fileRejectionError
						: acceptedFiles.length
							? uploadedText
							: null
					}
					{!!errorMessage && (
						<Meta
							className={styles.errorMessage}
							message={errorMessage}
						/>
					)}
				</div>
			</div>
		</div>
	);
};

export default Dropzone;
